// Add this at the top of the file
declare global {
    interface Window {
        wpSettings: {
            restNonce: string;
            wpRoot: string;
            wcRoot: string;
            siteUrl: string;
            wc_api: {
                consumer_key: string;
                consumer_secret: string;
            }
        }
    }
}

import { RcFile } from 'antd/es/upload';
import {
    WCProduct,
    WCOrder,
    WCCustomer,
    PalletCalculatorProduct,
    Pallet,
    OrderFile,
    WCSupplier,
    WCFDA,
    WPOption,
    OrderCapacityItem,
    WCContainer,
    SearchParams,
    PaginatedResponse,
    WCPallet,
    WCPalletLayout
} from 'types/wordpress';
import { parseBooleanMeta } from '../../utils/helpers';


export class WordPressAPI {


    // #region Common Functions
    // WordPress REST API headers
    private static getWPHeaders() {
        return {
            'X-WP-Nonce': window.wpSettings.restNonce,
            'Content-Type': 'application/json'
        };
    }


    // WooCommerce REST API headers
    private static getWCHeaders() {
        return {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.wpSettings.restNonce,
            'X-WC-Store-API-Nonce': window.wpSettings.restNonce
        };
    }


    // Generic request methods
    static async wpRequest(endpoint: string, options: RequestInit = {}) {
        const response = await fetch(`${window.wpSettings.wpRoot}${endpoint}`, {
            ...options,
            headers: {
                ...this.getWPHeaders(),
                ...(options.headers || {})
            }
        });
        return await response.json();
    }

    static async wcRequest(endpoint: string, options: RequestInit = {}) {
        const response = await fetch(`${window.wpSettings.wcRoot}${endpoint}`, {
            ...options,
            headers: {
                ...this.getWCHeaders(),
                ...(options.headers || {})
            },
            credentials: 'same-origin'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }



    //#endregion

    // #region Custom Post Types

    // #region Suppliers Api


    static async getSuppliers(params: SearchParams = {}): Promise<PaginatedResponse<WCSupplier>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wpRoot}supplier?page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWPHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const suppliers = await response.json();
            return {
                items: suppliers.map((supplier: any) => ({
                    id: supplier.id,
                    date_created: supplier.date,
                    date_modified: supplier.modified,
                    status: supplier.status,
                    // WordPress post fields
                    title: supplier.title.rendered,
                    slug: supplier.slug,
                    // Supplier meta fields
                    company_name: supplier.meta.company_name,
                    contact_name: supplier.meta.contact_name,
                    email: supplier.meta.email,
                    phone: supplier.meta.phone,
                    address: supplier.meta.address,
                    tax_id: supplier.meta.tax_id,
                    payment_terms: supplier.meta.payment_terms,
                    contacts: supplier.meta.contacts || [],
                    is_deleted: supplier.meta.is_deleted || false,
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            throw error;
        }
    }

    static async getSupplier(id: number): Promise<WCSupplier> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}supplier/${id}`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const supplier = await response.json();
            return {
                id: supplier.id,
                date_created: supplier.date,
                date_modified: supplier.modified,
                status: supplier.status,
                // WordPress post fields
                title: supplier.title.rendered,
                slug: supplier.slug,
                // Supplier meta fields
                company_name: supplier.meta.company_name,
                contact_name: supplier.meta.contact_name,
                email: supplier.meta.email,
                phone: supplier.meta.phone,
                address: supplier.meta.address,
                tax_id: supplier.meta.tax_id,
                payment_terms: supplier.meta.payment_terms,
                contacts: supplier.meta.contacts || [],
                is_deleted: supplier.meta.is_deleted || false,
            };
        } catch (error) {
            console.error('Error fetching supplier:', error);
            throw error;
        }
    }

    static async createSupplier(supplierData: Partial<WCSupplier>) {
        return await this.wpRequest('supplier', {
            method: 'POST',
            body: JSON.stringify({
                title: supplierData.company_name,
                status: 'publish',
                meta: {
                    company_name: supplierData.company_name,
                    contact_name: supplierData.contact_name,
                    email: supplierData.email,
                    phone: supplierData.phone,
                    address: supplierData.address,
                    tax_id: supplierData.tax_id,
                    payment_terms: supplierData.payment_terms,
                    contacts: supplierData.contacts || [],
                    is_deleted: supplierData.is_deleted || false
                }
            })
        });
    }

    static async updateSupplier(id: number, supplierData: Partial<WCSupplier>): Promise<WCSupplier> {
        return await this.wpRequest(`supplier/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: supplierData.company_name,
                meta: {
                    company_name: supplierData.company_name,
                    contact_name: supplierData.contact_name,
                    email: supplierData.email,
                    phone: supplierData.phone,
                    address: supplierData.address,
                    tax_id: supplierData.tax_id,
                    payment_terms: supplierData.payment_terms,
                    contacts: supplierData.contacts,
                    is_deleted: supplierData.is_deleted
                }
            })
        });
    }

    //#endregion


    // #region Pallets Api
    static async getPallets(params: SearchParams = {}): Promise<PaginatedResponse<WCPallet>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wpRoot}pallet?page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWPHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const pallets = await response.json();
            return {
                items: pallets.map((pallet: any) => ({
                    id: pallet.id,
                    date_created: pallet.date,
                    date_modified: pallet.modified,
                    status: pallet.status,
                    title: pallet.title,
                    slug: pallet.slug,
                    width: parseFloat(pallet.meta.width),
                    length: parseFloat(pallet.meta.length),
                    height: parseFloat(pallet.meta.height),
                    unitType: pallet.meta.unitType,
                    is_deleted: pallet.meta.is_deleted || false,
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching pallets:', error);
            throw error;
        }
    }

    static async getPallet(id: number): Promise<WCPallet> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}pallet/${id}`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const pallet = await response.json();
            return {
                id: pallet.id,
                date_created: pallet.date,
                date_modified: pallet.modified,
                status: pallet.status,
                slug: pallet.slug,
                title: pallet.title,
                width: parseFloat(pallet.meta.width),
                length: parseFloat(pallet.meta.length),
                height: parseFloat(pallet.meta.height),
                unitType: pallet.meta.unitType,
                is_deleted: pallet.meta.is_deleted || false,
            };
        } catch (error) {
            console.error('Error fetching pallet:', error);
            throw error;
        }
    }

    static async createPallet(palletData: Partial<WCPallet>): Promise<WCPallet> {
        return await this.wpRequest('pallet', {
            method: 'POST',
            body: JSON.stringify({
                title: palletData.title,
                status: 'publish',
                meta: {
                    width: palletData.width,
                    length: palletData.length,
                    height: palletData.height,
                    unitType: palletData.unitType,
                    is_deleted: palletData.is_deleted || false
                }
            })
        });
    }

    static async updatePallet(id: number, palletData: Partial<WCPallet>): Promise<WCPallet> {
        return await this.wpRequest(`pallet/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: palletData.title,
                meta: {
                    ...palletData.width && { width: palletData.width },
                    ...palletData.length && { length: palletData.length },
                    ...palletData.height && { height: palletData.height },
                    ...palletData.unitType && { unitType: palletData.unitType },
                    ...('is_deleted' in palletData) && { is_deleted: palletData.is_deleted }
                }
            })
        });
    }
    //#endregion


    // #region Pallet Layout Api
    static async getPalletLayouts(params: SearchParams = {}): Promise<PaginatedResponse<WCPalletLayout>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wpRoot}pallet_layout?page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWPHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const layouts = await response.json();
            return {
                // Start of Selection
                items: layouts.map(({ meta: { is_deleted = false, ...meta }, ...layout }) => ({
                    ...layout,
                    is_deleted,
                    meta
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching pallet layouts:', error);
            throw error;
        }
    }

    static async getPalletLayout(id: number): Promise<WCPalletLayout> {
        try {
            const layout = await this.wpRequest(`pallet_layout/${id}`);
            return {
                ...layout,
                is_deleted: layout.meta.is_deleted || false,
            };
        } catch (error) {
            console.error(`Error fetching pallet layout with id ${id}:`, error);
            throw error;
        }
    }

    static async createPalletLayout(layoutData: Partial<WCPalletLayout>): Promise<WCPalletLayout> {
        return await this.wpRequest('pallet_layout', {
            method: 'POST',
            body: JSON.stringify({
                title: layoutData.title,
                status: 'publish',
                meta: {
                    pallet_id: layoutData.meta?.pallet_id,
                    products: layoutData.meta?.products || [],
                    is_deleted: layoutData.is_deleted || false
                }
            })
        });
    }

    static async updatePalletLayout(id: number, layoutData: Partial<WCPalletLayout>): Promise<WCPalletLayout> {
        return await this.wpRequest(`pallet_layout/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: layoutData.title,
                meta: {
                    ...layoutData.meta?.pallet_id !== undefined && { pallet_id: layoutData.meta.pallet_id },
                    ...layoutData.meta?.products !== undefined && { products: layoutData.meta.products },
                    ...layoutData.is_deleted !== undefined && { is_deleted: layoutData.is_deleted }
                }
            })
        });
    }
    //#endregion

    // #region FDAs Api

    static async getFDAs(params: SearchParams = {}): Promise<PaginatedResponse<WCFDA>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wpRoot}fda?status=any&page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWPHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const fdas = await response.json();
            return {
                items: fdas.map((fda: any) => ({
                    id: fda.id,
                    date_created: fda.date,
                    date_modified: fda.modified,
                    status: fda.status,
                    // WordPress post fields
                    title: fda.title.rendered,
                    slug: fda.slug,
                    // FDA meta fields
                    name: fda.meta.name,
                    address: fda.meta.address,
                    telephone: fda.meta.telephone,
                    registration_number: fda.meta.registration_number,
                    expiry_date: fda.meta.expiry_date,
                    attachments: fda.meta.attachments || [],
                    is_deleted: fda.meta.is_deleted || false,
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching FDAs:', error);
            throw error;
        }
    }

    static async getFDA(id: number): Promise<WCFDA> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}fda/${id}`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const fda = await response.json();
            return {
                id: fda.id,
                date_created: fda.date,
                date_modified: fda.modified,
                status: fda.status,
                // WordPress post fields
                title: fda.title.rendered,
                slug: fda.slug,
                // FDA meta fields
                name: fda.meta.name,
                address: fda.meta.address,
                telephone: fda.meta.telephone,
                registration_number: fda.meta.registration_number,
                expiry_date: fda.meta.expiry_date,
                attachments: fda.meta.attachments || [],
                is_deleted: fda.meta.is_deleted || false,
            };
        } catch (error) {
            console.error('Error fetching FDA:', error);
            throw error;
        }
    }

    static async createFDA(fdaData: Partial<WCFDA>) {
        const cleanedAttachments = fdaData.attachments?.map(({ uid, status, ...rest }: any) => rest);
        return await this.wpRequest('fda', {
            method: 'POST',
            body: JSON.stringify({
                title: fdaData.name,
                status: 'publish',
                meta: {
                    name: fdaData.name,
                    address: fdaData.address,
                    telephone: fdaData.telephone,
                    registration_number: fdaData.registration_number,
                    expiry_date: fdaData.expiry_date,
                    attachments: cleanedAttachments,
                    is_deleted: fdaData.is_deleted || false
                }
            })
        });
    }

    static async updateFDA(id: number, fdaData: Partial<WCFDA>): Promise<WCFDA> {
        const cleanedAttachments = fdaData.attachments?.map(({ uid, status, ...rest }: any) => rest);
        return await this.wpRequest(`fda/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: fdaData.name,
                meta: {
                    ...fdaData.name && { name: fdaData.name },
                    ...fdaData.address && { address: fdaData.address },
                    ...fdaData.telephone && { telephone: fdaData.telephone },
                    ...fdaData.registration_number && { registration_number: fdaData.registration_number },
                    ...fdaData.expiry_date && { expiry_date: fdaData.expiry_date },
                    ...cleanedAttachments && { attachments: cleanedAttachments },
                    ...('is_deleted' in fdaData) && { is_deleted: fdaData.is_deleted }
                }
            })
        });
    }

    static async uploadFDAFile(file: RcFile, postId?: number) {
        const formData = new FormData();
        formData.append('file', file);
        if (postId) {
            formData.append('post_id', postId.toString());
        }

        const headers = Object.fromEntries(
            Object.entries(this.getWPHeaders())
                .filter(([key]) => key !== 'Content-Type')
        );

        const response = await fetch(
            `${window.wpSettings.wcRoot}fda/upload`,
            {
                method: 'POST',
                headers,
                body: formData
            }
        );

        if (!response.ok) {
            throw new Error('Upload failed');
        }

        const data = await response.json();
        return data;
    }

    //#endregion

    // #region Container
    static async getContainers(): Promise<WCContainer[]> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}container`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const containers = await response.json();
            return containers.map((container: any) => ({
                id: container.id,
                date_created: container.date,
                date_modified: container.modified,
                status: container.status,
                // WordPress post fields
                title: container.title,
                slug: container.slug,
                // Container meta fields
                common_sizes: container.meta.common_sizes,
                external_dimensions_length: container.meta.external_dimensions_length,
                external_dimensions_width: container.meta.external_dimensions_width,
                external_dimensions_height: container.meta.external_dimensions_height,
                internal_dimensions_length: container.meta.internal_dimensions_length,
                internal_dimensions_width: container.meta.internal_dimensions_width,
                internal_dimensions_height: container.meta.internal_dimensions_height,
                is_deleted: container.meta.is_deleted || false,
            }));
        } catch (error) {
            console.error('Error fetching containers:', error);
            throw error;
        }
    }

    static async getContainer(id: number): Promise<WCContainer> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}container/${id}`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const container = await response.json();
            return {
                id: container.id,
                date_created: container.date,
                date_modified: container.modified,
                status: container.status,
                // WordPress post fields
                title: container.title,
                slug: container.slug,
                // Container meta fields
                common_sizes: container.meta.common_sizes,
                external_dimensions_length: container.meta.external_dimensions_length,
                external_dimensions_width: container.meta.external_dimensions_width,
                external_dimensions_height: container.meta.external_dimensions_height,
                internal_dimensions_length: container.meta.internal_dimensions_length,
                internal_dimensions_width: container.meta.internal_dimensions_width,
                internal_dimensions_height: container.meta.internal_dimensions_height,
                is_deleted: container.meta.is_deleted || false,
            };
        } catch (error) {
            console.error('Error fetching container:', error);
            throw error;
        }
    }

    static async createContainer(containerData: Partial<WCContainer>): Promise<WCContainer> {
        return await this.wpRequest('container', {
            method: 'POST',
            body: JSON.stringify({
                title: containerData.title?.rendered || containerData.title,
                status: 'publish',
                meta: {
                    common_sizes: containerData.common_sizes,
                    external_dimensions_length: containerData.external_dimensions_length,
                    external_dimensions_width: containerData.external_dimensions_width,
                    external_dimensions_height: containerData.external_dimensions_height,
                    internal_dimensions_length: containerData.internal_dimensions_length,
                    internal_dimensions_width: containerData.internal_dimensions_width,
                    internal_dimensions_height: containerData.internal_dimensions_height,
                    is_deleted: containerData.is_deleted || false
                }
            })
        });
    }

    static async updateContainer(id: number, containerData: Partial<WCContainer>): Promise<WCContainer> {
        return await this.wpRequest(`container/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: containerData.title?.rendered || containerData.title,
                meta: {
                    ...containerData.common_sizes && { common_sizes: containerData.common_sizes },
                    ...containerData.external_dimensions_length && { external_dimensions_length: containerData.external_dimensions_length },
                    ...containerData.external_dimensions_width && { external_dimensions_width: containerData.external_dimensions_width },
                    ...containerData.external_dimensions_height && { external_dimensions_height: containerData.external_dimensions_height },
                    ...containerData.internal_dimensions_length && { internal_dimensions_length: containerData.internal_dimensions_length },
                    ...containerData.internal_dimensions_width && { internal_dimensions_width: containerData.internal_dimensions_width },
                    ...containerData.internal_dimensions_height && { internal_dimensions_height: containerData.internal_dimensions_height },
                    ...('is_deleted' in containerData) && { is_deleted: containerData.is_deleted }
                }
            })
        });
    }

    static async deleteContainer(id: number): Promise<void> {
        await this.wpRequest(`container/${id}`, {
            method: 'DELETE'
        });
    }
    // #endregion 


    // #region Order Files Api
    static async getOrderFiles(params: SearchParams = {}): Promise<PaginatedResponse<OrderFile>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wpRoot}order_file?status=any&page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWPHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const orderFiles = await response.json();
            return {
                items: orderFiles.map((orderFile: any) => ({
                    id: orderFile.id,
                    date_created: orderFile.date,
                    date_modified: orderFile.modified,
                    status: orderFile.status,
                    title: orderFile.title,
                    slug: orderFile.slug,
                    container: orderFile.meta.container,
                    order: orderFile.meta.order,
                    customer: orderFile.meta.customer,
                    profit_margin: orderFile.meta.profit_margin,
                    profit_margin_approved: orderFile.meta.profit_margin_approved || false,
                    shipping_price: orderFile.meta.shipping_price,
                    authorized_letter_provided: orderFile.meta.authorized_letter_provided || false,
                    certification_of_origin_provided: orderFile.meta.certification_of_origin_provided || false,
                    final_certification_of_origin_provided: orderFile.meta.final_certification_of_origin_provided || false,
                    booking_number: orderFile.meta.booking_number,
                    booking_date: orderFile.meta.booking_date,
                    loading_date: orderFile.meta.loading_date,
                    shipping_date: orderFile.meta.shipping_date,
                    real_shipping_date: orderFile.meta.real_shipping_date,
                    goods_are_ready_date: orderFile.meta.goods_are_ready_date,
                    container_number: orderFile.meta.container_number,
                    bill_of_lading_number: orderFile.meta.bill_of_lading_number,
                    arrival_date: orderFile.meta.arrival_date,
                    real_arrival_date: orderFile.meta.real_arrival_date,
                    short_invoice_shared: orderFile.meta.short_invoice_shared || false,
                    short_invoice_shared_date: orderFile.meta.short_invoice_shared_date,
                    long_invoice_shared: orderFile.meta.long_invoice_shared || false,
                    long_invoice_shared_date: orderFile.meta.long_invoice_shared_date,
                    invoice_shared: orderFile.meta.invoice_shared || false,
                    invoice_shared_date: orderFile.meta.invoice_shared_date,
                    client_received_container: orderFile.meta.client_received_container || false,
                    client_received_container_date: orderFile.meta.client_received_container_date,
                    contains_rise: orderFile.meta.contains_rise || false,
                    contains_medicine: orderFile.meta.contains_medicine || false,
                    contains_ghee: orderFile.meta.contains_ghee || false,
                    client_payments: orderFile.meta.client_payments || [],
                    order_payments: orderFile.meta.order_payments || [],
                    order_file_attachments: orderFile.meta.order_file_attachments || [],
                    notes: orderFile.meta.notes || '',
                    is_deleted: orderFile.meta.is_deleted || false,
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching order files:', error);
            throw error;
        }
    }

    static async getOrderFile(id: number): Promise<OrderFile> {
        try {
            const response = await fetch(`${window.wpSettings.wpRoot}order_file/${id}?status=any`, {
                headers: this.getWPHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const orderFile = await response.json();
            return {
                id: orderFile.id,
                date_created: orderFile.date,
                date_modified: orderFile.modified,
                status: orderFile.status,
                // WordPress post fields
                title: orderFile.title,
                slug: orderFile.slug,
                // Order file meta fields
                container: orderFile.meta.container,
                order: orderFile.meta.order,
                customer: orderFile.meta.customer,
                profit_margin: orderFile.meta.profit_margin,
                profit_margin_approved: orderFile.meta.profit_margin_approved || false,
                shipping_price: orderFile.meta.shipping_price,
                authorized_letter_provided: orderFile.meta.authorized_letter_provided || false,
                certification_of_origin_provided: orderFile.meta.certification_of_origin_provided || false,
                final_certification_of_origin_provided: orderFile.meta.final_certification_of_origin_provided || false,
                booking_number: orderFile.meta.booking_number,
                booking_date: orderFile.meta.booking_date,
                loading_date: orderFile.meta.loading_date,
                shipping_date: orderFile.meta.shipping_date,
                real_shipping_date: orderFile.meta.real_shipping_date,
                goods_are_ready_date: orderFile.meta.goods_are_ready_date,
                container_number: orderFile.meta.container_number,
                bill_of_lading_number: orderFile.meta.bill_of_lading_number,
                arrival_date: orderFile.meta.arrival_date,
                real_arrival_date: orderFile.meta.real_arrival_date,
                short_invoice_shared: orderFile.meta.short_invoice_shared || false,
                short_invoice_shared_date: orderFile.meta.short_invoice_shared_date,
                long_invoice_shared: orderFile.meta.long_invoice_shared || false,
                long_invoice_shared_date: orderFile.meta.long_invoice_shared_date,
                invoice_shared: orderFile.meta.invoice_shared || false,
                invoice_shared_date: orderFile.meta.invoice_shared_date,
                client_received_container: orderFile.meta.client_received_container || false,
                client_received_container_date: orderFile.meta.client_received_container_date,
                contains_rise: orderFile.meta.contains_rise || false,
                contains_medicine: orderFile.meta.contains_medicine || false,
                contains_ghee: orderFile.meta.contains_ghee || false,
                client_payments: orderFile.meta.client_payments || [],
                order_payments: orderFile.meta.order_payments || [],
                order_file_attachments: orderFile.meta.order_file_attachments || [],
                notes: orderFile.meta.notes || '',
                is_deleted: orderFile.meta.is_deleted || false,
            };
        } catch (error) {
            console.error('Error fetching order file:', error);
            throw error;
        }
    }

    static async createOrderFile(orderFileData: Partial<OrderFile>): Promise<OrderFile> {
        const cleanedAttachments = orderFileData.order_file_attachments?.map(({ uid, status, ...rest }: any) => rest);
        return await this.wpRequest('order_file', {
            method: 'POST',
            body: JSON.stringify({
                title: orderFileData.title?.rendered || orderFileData.title,
                status: orderFileData.status || 'draft',
                meta: {
                    container: orderFileData.container,
                    order: orderFileData.order,
                    customer: orderFileData.customer,
                    profit_margin: orderFileData.profit_margin,
                    profit_margin_approved: orderFileData.profit_margin_approved || false,
                    shipping_price: orderFileData.shipping_price,
                    authorized_letter_provided: orderFileData.authorized_letter_provided || false,
                    certification_of_origin_provided: orderFileData.certification_of_origin_provided || false,
                    final_certification_of_origin_provided: orderFileData.final_certification_of_origin_provided || false,
                    booking_number: orderFileData.booking_number,
                    booking_date: orderFileData.booking_date,
                    loading_date: orderFileData.loading_date,
                    shipping_date: orderFileData.shipping_date,
                    real_shipping_date: orderFileData.real_shipping_date,
                    goods_are_ready_date: orderFileData.goods_are_ready_date,
                    container_number: orderFileData.container_number,
                    bill_of_lading_number: orderFileData.bill_of_lading_number,
                    arrival_date: orderFileData.arrival_date,
                    real_arrival_date: orderFileData.real_arrival_date,
                    short_invoice_shared: orderFileData.short_invoice_shared || false,
                    short_invoice_shared_date: orderFileData.short_invoice_shared_date,
                    long_invoice_shared: orderFileData.long_invoice_shared || false,
                    long_invoice_shared_date: orderFileData.long_invoice_shared_date,
                    invoice_shared: orderFileData.invoice_shared || false,
                    invoice_shared_date: orderFileData.invoice_shared_date,
                    client_received_container: orderFileData.client_received_container || false,
                    client_received_container_date: orderFileData.client_received_container_date,
                    contains_rise: orderFileData.contains_rise || false,
                    contains_medicine: orderFileData.contains_medicine || false,
                    contains_ghee: orderFileData.contains_ghee || false,
                    client_payments: orderFileData.client_payments || [],
                    order_payments: orderFileData.order_payments || [],
                    order_file_attachments: cleanedAttachments || [],
                    notes: orderFileData.notes || '',
                    is_deleted: orderFileData.is_deleted || false,
                }
            })
        });
    }

    static async updateOrderFile(id: number, orderFileData: Partial<OrderFile>): Promise<OrderFile> {
        const cleanedAttachments = orderFileData.order_file_attachments?.map(({ uid, status, ...rest }: any) => rest);
        return await this.wpRequest(`order_file/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                title: orderFileData.title?.rendered || orderFileData.title,
                status: orderFileData.status,
                meta: {
                    ...orderFileData.container !== undefined && { container: orderFileData.container },
                    ...orderFileData.order !== undefined && { order: orderFileData.order },
                    ...orderFileData.customer !== undefined && { customer: orderFileData.customer },
                    ...orderFileData.profit_margin !== undefined && { profit_margin: orderFileData.profit_margin },
                    ...orderFileData.profit_margin_approved !== undefined && { profit_margin_approved: orderFileData.profit_margin_approved },
                    ...orderFileData.shipping_price !== undefined && { shipping_price: orderFileData.shipping_price },
                    ...orderFileData.authorized_letter_provided !== undefined && { authorized_letter_provided: orderFileData.authorized_letter_provided },
                    ...orderFileData.certification_of_origin_provided !== undefined && { certification_of_origin_provided: orderFileData.certification_of_origin_provided },
                    ...orderFileData.final_certification_of_origin_provided !== undefined && { final_certification_of_origin_provided: orderFileData.final_certification_of_origin_provided },
                    ...orderFileData.booking_number !== undefined && { booking_number: orderFileData.booking_number },
                    ...orderFileData.booking_date !== undefined && { booking_date: orderFileData.booking_date },
                    ...orderFileData.loading_date !== undefined && { loading_date: orderFileData.loading_date },
                    ...orderFileData.shipping_date !== undefined && { shipping_date: orderFileData.shipping_date },
                    ...orderFileData.real_shipping_date !== undefined && { real_shipping_date: orderFileData.real_shipping_date },
                    ...orderFileData.goods_are_ready_date !== undefined && { goods_are_ready_date: orderFileData.goods_are_ready_date },
                    ...orderFileData.container_number !== undefined && { container_number: orderFileData.container_number },
                    ...orderFileData.bill_of_lading_number !== undefined && { bill_of_lading_number: orderFileData.bill_of_lading_number },
                    ...orderFileData.arrival_date !== undefined && { arrival_date: orderFileData.arrival_date },
                    ...orderFileData.real_arrival_date !== undefined && { real_arrival_date: orderFileData.real_arrival_date },
                    ...orderFileData.short_invoice_shared !== undefined && { short_invoice_shared: orderFileData.short_invoice_shared },
                    ...orderFileData.short_invoice_shared_date !== undefined && { short_invoice_shared_date: orderFileData.short_invoice_shared_date },
                    ...orderFileData.long_invoice_shared !== undefined && { long_invoice_shared: orderFileData.long_invoice_shared },
                    ...orderFileData.long_invoice_shared_date !== undefined && { long_invoice_shared_date: orderFileData.long_invoice_shared_date },
                    ...orderFileData.invoice_shared !== undefined && { invoice_shared: orderFileData.invoice_shared },
                    ...orderFileData.invoice_shared_date !== undefined && { invoice_shared_date: orderFileData.invoice_shared_date },
                    ...orderFileData.client_received_container !== undefined && { client_received_container: orderFileData.client_received_container },
                    ...orderFileData.client_received_container_date !== undefined && { client_received_container_date: orderFileData.client_received_container_date },
                    ...orderFileData.contains_rise !== undefined && { contains_rise: orderFileData.contains_rise },
                    ...orderFileData.contains_medicine !== undefined && { contains_medicine: orderFileData.contains_medicine },
                    ...orderFileData.contains_ghee !== undefined && { contains_ghee: orderFileData.contains_ghee },
                    ...orderFileData.client_payments !== undefined && { client_payments: orderFileData.client_payments },
                    ...orderFileData.order_payments !== undefined && { order_payments: orderFileData.order_payments },
                    ...cleanedAttachments && { order_file_attachments: cleanedAttachments },
                    ...orderFileData.notes !== undefined && { notes: orderFileData.notes },
                    ...orderFileData.is_deleted !== undefined && { is_deleted: orderFileData.is_deleted },
                }
            })
        });
    }

    static async deleteOrderFile(id: number): Promise<void> {
        await this.wpRequest(`order_file/${id}`, {
            method: 'DELETE'
        });
    }
    //#endregion
    // #endregion

    // #region WooCommerce endpoints

    // #region WooCommerce Products
    static async getProducts(params: SearchParams = {}): Promise<PaginatedResponse<WCProduct>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await fetch(
                `${window.wpSettings.wcRoot}products?page=${page}&per_page=${per_page}${searchParam}`,
                { headers: this.getWCHeaders() }
            );

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const products = await response.json();
            return {
                items: products.map((product: any) => ({
                    id: product.id,
                    date_created: product.date_created,
                    date_modified: product.date_modified,
                    status: product.status,
                    name: product.name,
                    slug: product.slug,
                    price: product.price,
                    regular_price: product.regular_price,
                    sale_price: product.sale_price || '',
                    date_on_sale_from: product.date_on_sale_from || '',
                    date_on_sale_to: product.date_on_sale_to || '',
                    sku: product.sku,
                    stock_status: product.stock_status,
                    stock_quantity: product.stock_quantity,
                    categories: product.categories || [],
                    dimensions: {
                        length: product.dimensions?.length || '',
                        width: product.dimensions?.width || '',
                        height: product.dimensions?.height || ''
                    },
                    weight: product.weight || '',
                    // Meta fields
                    barcode: product.meta_data?.find((m: any) => m.key === 'barcode')?.value || '',
                    hts_code: product.meta_data?.find((m: any) => m.key === 'hts_code')?.value || '',
                    unit_per_boxkg: product.meta_data?.find((m: any) => m.key === 'unit_per_boxkg')?.value || '',
                    last_price: product.meta_data?.find((m: any) => m.key === 'last_price')?.value || '',
                    unit_per_box_count: product.meta_data?.find((m: any) => m.key === 'unit_per_box_count')?.value || '',
                    unit_per_box_weight_g: product.meta_data?.find((m: any) => m.key === 'unit_per_box_weight_g')?.value || '',
                    fda: product.meta_data?.find((m: any) => m.key === 'fda')?.value || '',
                    is_deleted: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'is_deleted')?.value),
                    product_attachments: product.meta_data?.find((m: any) => m.key === 'product_attachments')?.value || [],
                    can_fill_pallet_gaps: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'can_fill_pallet_gaps')?.value),
                    can_be_rotated: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'can_be_rotated')?.value),
                    preferred_pallet_type: product.meta_data?.find((m: any) => m.key === 'preferred_pallet_type')?.value || '',
                    max_stack_height: product.meta_data?.find((m: any) => m.key === 'max_stack_height')?.value || '',
                    max_stack_weight: product.meta_data?.find((m: any) => m.key === 'max_stack_weight')?.value || '',
                    unit_packaging: product.meta_data?.find((m: any) => m.key === 'unit_packaging')?.value || '',
                    images: product.images?.map((img: any) => ({
                        id: img.id,
                        src: img.src,
                        name: img.name,
                        alt: img.alt
                    })) || [],
                })),
                total: parseInt(response.headers.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    }

    static async searchProducts(query: string, searchType: 'barcode' | 'name' | 'sku' | 'fda'): Promise<WCProduct[]> {
        console.log('Searching products with query:', query, 'type:', searchType);

        const response = await fetch(`/wp-json/react-pallet-calculator/v1/search-products?term=${encodeURIComponent(query)}&type=${searchType}`, {
            headers: this.getWCHeaders()
        });

        if (!response.ok) {
            throw new Error('Failed to search products');
        }

        const data = await response.json();
        return data;
    }

    static async getProduct(id: number): Promise<WCProduct> {
        try {
            const response = await fetch(`${window.wpSettings.wcRoot}products/${id}`, {
                headers: this.getWCHeaders()
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const product = await response.json();
            return {
                id: product.id,
                date_created: product.date_created,
                date_modified: product.date_modified,
                status: product.status,
                name: product.name,
                slug: product.slug,
                price: product.price,
                regular_price: product.regular_price,
                sale_price: product.sale_price || '',
                date_on_sale_from: product.date_on_sale_from || '',
                date_on_sale_to: product.date_on_sale_to || '',
                sku: product.sku,
                stock_status: product.stock_status,
                stock_quantity: product.stock_quantity,
                categories: product.categories || [],
                dimensions: {
                    length: product.dimensions?.length || '',
                    width: product.dimensions?.width || '',
                    height: product.dimensions?.height || ''
                },
                weight: product.weight || '',
                // Meta fields
                barcode: product.meta_data?.find((m: any) => m.key === 'barcode')?.value || '',
                hts_code: product.meta_data?.find((m: any) => m.key === 'hts_code')?.value || '',
                unit_per_boxkg: product.meta_data?.find((m: any) => m.key === 'unit_per_boxkg')?.value || '',
                last_price: product.meta_data?.find((m: any) => m.key === 'last_price')?.value || '',
                unit_per_box_count: product.meta_data?.find((m: any) => m.key === 'unit_per_box_count')?.value || '',
                unit_per_box_weight_g: product.meta_data?.find((m: any) => m.key === 'unit_per_box_weight_g')?.value || '',
                fda: product.meta_data?.find((m: any) => m.key === 'fda')?.value || '',
                is_deleted: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'is_deleted')?.value),
                product_attachments: product.meta_data?.find((m: any) => m.key === 'product_attachments')?.value || [],
                can_fill_pallet_gaps: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'can_fill_pallet_gaps')?.value),
                can_be_rotated: parseBooleanMeta(product.meta_data?.find((m: any) => m.key === 'can_be_rotated')?.value),
                preferred_pallet_type: product.meta_data?.find((m: any) => m.key === 'preferred_pallet_type')?.value || '',
                max_stack_height: product.meta_data?.find((m: any) => m.key === 'max_stack_height')?.value || '',
                max_stack_weight: product.meta_data?.find((m: any) => m.key === 'max_stack_weight')?.value || '',
                unit_packaging: product.meta_data?.find((m: any) => m.key === 'unit_packaging')?.value || '',
                images: product.images?.map((img: any) => ({
                    id: img.id,
                    src: img.src,
                    name: img.name,
                    alt: img.alt
                })) || [],
            };
        } catch (error) {
            console.error('Error fetching product:', error);
            throw error;
        }
    }

    static async createProduct(productData: Partial<WCProduct>): Promise<WCProduct> {
        const wcProductData = {
            name: productData.name,
            regular_price: productData.regular_price?.toString() || '',
            sale_price: productData.sale_price?.toString() || '',
            date_on_sale_from: productData.date_on_sale_from || '',
            date_on_sale_to: productData.date_on_sale_to || '',
            sku: productData.sku || '',
            stock_status: productData.stock_status || 'instock',
            stock_quantity: productData.stock_quantity || 0,
            categories: productData.categories?.map(cat => ({ id: cat.id })) || [],
            dimensions: {
                length: productData.dimensions?.length?.toString() || '',
                width: productData.dimensions?.width?.toString() || '',
                height: productData.dimensions?.height?.toString() || ''
            },
            weight: productData.weight?.toString() || '',
            meta_data: [
                ...(productData.barcode !== undefined ? [{ key: 'barcode', value: productData.barcode }] : []),
                ...(productData.hts_code !== undefined ? [{ key: 'hts_code', value: productData.hts_code }] : []),
                ...(productData.unit_per_boxkg !== undefined ? [{ key: 'unit_per_boxkg', value: productData.unit_per_boxkg }] : []),
                ...(productData.last_price !== undefined ? [{ key: 'last_price', value: productData.last_price }] : []),
                ...(productData.unit_per_box_count !== undefined ? [{ key: 'unit_per_box_count', value: productData.unit_per_box_count }] : []),
                ...(productData.unit_per_box_weight_g !== undefined ? [{ key: 'unit_per_box_weight_g', value: productData.unit_per_box_weight_g }] : []),
                ...(productData.fda !== undefined ? [{ key: 'fda', value: productData.fda }] : []),
                ...(productData.is_deleted !== undefined ? [{ key: 'is_deleted', value: productData.is_deleted }] : []),
                ...(productData.product_attachments !== undefined ? [{ key: 'product_attachments', value: productData.product_attachments }] : []),
                ...(productData.can_fill_pallet_gaps !== undefined ? [{ key: 'can_fill_pallet_gaps', value: productData.can_fill_pallet_gaps }] : []),
                ...(productData.can_be_rotated !== undefined ? [{ key: 'can_be_rotated', value: productData.can_be_rotated }] : []),
                ...(productData.preferred_pallet_type !== undefined ? [{ key: 'preferred_pallet_type', value: productData.preferred_pallet_type }] : []),
                ...(productData.max_stack_height !== undefined ? [{ key: 'max_stack_height', value: productData.max_stack_height }] : []),
                ...(productData.max_stack_weight !== undefined ? [{ key: 'max_stack_weight', value: productData.max_stack_weight }] : []),
                ...(productData.unit_packaging !== undefined ? [{ key: 'unit_packaging', value: productData.unit_packaging }] : [])
            ]
        };

        return await this.wcRequest('products', {
            method: 'POST',
            body: JSON.stringify(wcProductData)
        });
    }

    static async updateProduct(id: number, productData: Partial<WCProduct>): Promise<WCProduct> {
        const data: any = {
            ...productData.name && { name: productData.name },
            ...productData.regular_price && {
                regular_price: productData.
                    regular_price.toString()
            },
            ...(productData.sale_price !== undefined && {
                sale_price: productData.sale_price || null,
                date_on_sale_from: productData.sale_price ? productData.
                    date_on_sale_from : null,
                date_on_sale_to: productData.sale_price ? productData.
                    date_on_sale_to : null
            }),
            ...productData.categories && {
                categories: productData.categories
            },
            ...productData.dimensions && {
                dimensions: {
                    length: productData.dimensions.length.toString(),
                    width: productData.dimensions.width.toString(),
                    height: productData.dimensions.height.toString()
                }
            },
            ...productData.weight && { weight: productData.weight.toString() },
            ...(productData.images && { images: productData.images.map(img => ({ id: img.id })) }),
            meta_data: [
                ...(productData.barcode !== undefined ? [{
                    key: 'barcode',
                    value: productData.barcode
                }] : []),
                ...(productData.last_price !== undefined ? [{
                    key:
                        'last_price', value: productData.last_price
                }] : []),
                ...(productData.can_fill_pallet_gaps !== undefined ? [{
                    key:
                        'can_fill_pallet_gaps', value: productData.
                            can_fill_pallet_gaps
                }] : []),
                ...(productData.can_be_rotated !== undefined ? [{
                    key:
                        'can_be_rotated', value: productData.can_be_rotated
                }] :
                    []),
                ...(productData.max_stack_height !== undefined ? [{
                    key:
                        'max_stack_height', value: productData.max_stack_height
                }]
                    : []),
                ...(productData.unit_packaging !== undefined ? [{
                    key:
                        'unit_packaging', value: productData.unit_packaging
                }] : []),
                ...(productData.is_deleted !== undefined ? [{
                    key:
                        'is_deleted', value: productData.is_deleted
                }] : [])
            ],

        };

        return await this.wcRequest(`products/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }


    static async uploadMedia(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);

        const headers = {
            'X-WP-Nonce': window.wpSettings.restNonce,
        };

        return await fetch(`${window.wpSettings.wpRoot}media`, {
            method: 'POST',
            headers: headers,
            body: formData,
            credentials: 'same-origin'
        }).then(response => response.json());
    }
    //#endregion

    // #region Categories API
    static async getCategories(): Promise<{ id: number; name: string; slug: string; }[]> {
        try {
            const response = await this.wcRequest('products/categories', {
                method: 'GET'
            });

            return response.map((category: any) => ({
                id: category.id,
                name: category.name,
                slug: category.slug
            }));
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }
    //#endregion


    // #region WooCommerce customers
    static async getCustomers(params: SearchParams = {}): Promise<PaginatedResponse<WCCustomer>> {
        try {
            const { page = 1, per_page = 10, search = '' } = params;
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

            const response = await this.wcRequest(
                `customers?page=${page}&per_page=${per_page}${searchParam}`,
                { method: 'GET' }
            );

            return {
                items: response.map((customer: any) => ({
                    id: customer.id,
                    first_name: customer.first_name,
                    email: customer.email,
                    title: customer.title || { rendered: '' },
                    status: customer.status || 'publish',
                    date: customer.date || '',
                    modified: customer.modified || '',

                    // Meta fields
                    contacts: customer.meta_data?.find((m: any) => m.key === 'contacts')?.value || [],
                    is_deleted: customer.meta_data?.find((m: any) => m.key === 'is_deleted')?.value || false,
                    order_profit_percentage: Number(customer.meta_data?.find((m: any) => m.key === 'order_profit_percentage')?.value) || 0,
                    item_profit_percentage: Number(customer.meta_data?.find((m: any) => m.key === 'item_profit_percentage')?.value) || 0,
                    discount_percentage: Number(customer.meta_data?.find((m: any) => m.key === 'discount_percentage')?.value) || 0,
                    cashback_percentage: Number(customer.meta_data?.find((m: any) => m.key === 'cashback_percentage')?.value) || 0,
                    address: customer.meta_data?.find((m: any) => m.key === 'address')?.value || '',
                    city: customer.meta_data?.find((m: any) => m.key === 'city')?.value || '',
                    state: customer.meta_data?.find((m: any) => m.key === 'state')?.value || '',
                    zip_code: customer.meta_data?.find((m: any) => m.key === 'zip_code')?.value || '',
                    fda: customer.meta_data?.find((m: any) => m.key === 'fda')?.value || '',
                    telephone: customer.meta_data?.find((m: any) => m.key === 'telephone')?.value || ''
                })),
                total: parseInt(response.headers?.get('X-WP-Total') || '0'),
                totalPages: parseInt(response.headers?.get('X-WP-TotalPages') || '0'),
                currentPage: page
            };
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    }

    static async createCustomer(customerData: Partial<WCCustomer>): Promise<WCCustomer> {
        const email = customerData.email ||
            customerData.contacts?.find(c => c.is_default)?.email ||
            customerData.contacts?.[0]?.email ||
            '';

        return await this.wcRequest('customers', {
            method: 'POST',
            body: JSON.stringify({
                first_name: customerData.first_name,
                email: email,
                meta_data: [
                    { key: 'contacts', value: customerData.contacts || [] },
                    { key: 'is_deleted', value: customerData.is_deleted || false },
                    { key: 'order_profit_percentage', value: customerData.order_profit_percentage || 0 },
                    { key: 'item_profit_percentage', value: customerData.item_profit_percentage || 0 },
                    { key: 'discount_percentage', value: customerData.discount_percentage || 0 },
                    { key: 'cashback_percentage', value: customerData.cashback_percentage || 0 },
                    { key: 'address', value: customerData.address || '' },
                    { key: 'city', value: customerData.city || '' },
                    { key: 'state', value: customerData.state || '' },
                    { key: 'zip_code', value: customerData.zip_code || '' },
                    { key: 'fda', value: customerData.fda || '' },
                    { key: 'telephone', value: customerData.telephone || '' }
                ]
            })
        });
    }

    static async updateCustomer(id: number, customerData: Partial<WCCustomer>): Promise<WCCustomer> {
        const email = customerData.email ||
            customerData.contacts?.find(c => c.is_default)?.email ||
            customerData.contacts?.[0]?.email;

        const meta_data: Array<{ key: string; value: any }> = [];

        if ('contacts' in customerData) meta_data.push({ key: 'contacts', value: customerData.contacts });
        if ('is_deleted' in customerData) meta_data.push({ key: 'is_deleted', value: customerData.is_deleted });
        if ('order_profit_percentage' in customerData) meta_data.push({ key: 'order_profit_percentage', value: customerData.order_profit_percentage });
        if ('item_profit_percentage' in customerData) meta_data.push({ key: 'item_profit_percentage', value: customerData.item_profit_percentage });
        if ('discount_percentage' in customerData) meta_data.push({ key: 'discount_percentage', value: customerData.discount_percentage });
        if ('cashback_percentage' in customerData) meta_data.push({ key: 'cashback_percentage', value: customerData.cashback_percentage });
        if ('address' in customerData) meta_data.push({ key: 'address', value: customerData.address });
        if ('city' in customerData) meta_data.push({ key: 'city', value: customerData.city });
        if ('state' in customerData) meta_data.push({ key: 'state', value: customerData.state });
        if ('zip_code' in customerData) meta_data.push({ key: 'zip_code', value: customerData.zip_code });
        if ('fda' in customerData) meta_data.push({ key: 'fda', value: customerData.fda });
        if ('telephone' in customerData) meta_data.push({ key: 'telephone', value: customerData.telephone });

        return await this.wcRequest(`customers/${id}`, {
            method: 'PUT',
            body: JSON.stringify({
                ...customerData.first_name && { first_name: customerData.first_name },
                ...(email && { email }),
                meta_data
            })
        });
    }

    static async deleteCustomer(id: number): Promise<void> {
        const response = await fetch(`${window.wpSettings.wcRoot}customers/${id}`, {
            method: 'DELETE',
            headers: this.getWCHeaders(),
        });
        if (!response.ok) throw new Error('Failed to delete customer');
    }

    static async getCustomer(id: number): Promise<WCCustomer> {
        try {
            const response = await this.wcRequest(`customers/${id}`, {
                method: 'GET'
            });

            return {
                id: response.id,
                first_name: response.first_name,
                email: response.email,
                title: response.title || { rendered: '' },
                status: response.status || 'publish',
                date: response.date || '',
                modified: response.modified || '',
                // Meta fields
                contacts: response.meta_data?.find((m: any) => m.key === 'contacts')?.value || [],
                is_deleted: response.meta_data?.find((m: any) => m.key === 'is_deleted')?.value || false,
                order_profit_percentage: Number(response.meta_data?.find((m: any) => m.key === 'order_profit_percentage')?.value) || 0,
                item_profit_percentage: Number(response.meta_data?.find((m: any) => m.key === 'item_profit_percentage')?.value) || 0,
                discount_percentage: Number(response.meta_data?.find((m: any) => m.key === 'discount_percentage')?.value) || 0,
                cashback_percentage: Number(response.meta_data?.find((m: any) => m.key === 'cashback_percentage')?.value) || 0,
                address: response.meta_data?.find((m: any) => m.key === 'address')?.value || '',
                city: response.meta_data?.find((m: any) => m.key === 'city')?.value || '',
                state: response.meta_data?.find((m: any) => m.key === 'state')?.value || '',
                zip_code: response.meta_data?.find((m: any) => m.key === 'zip_code')?.value || '',
                fda: response.meta_data?.find((m: any) => m.key === 'fda')?.value || '',
                telephone: response.meta_data?.find((m: any) => m.key === 'telephone')?.value || ''
            };
        } catch (error) {
            console.error('Error fetching customer:', error);
            throw error;
        }
    }

    //#endregion
    // #endregion

    //#region Options
    static async getOptions(): Promise<WPOption[]> {
        const response = await fetch(`/wp-json/pallet-calculator/v1/options`, {
            headers: this.getWPHeaders()
        });
        return response.json();
    }

    static async updateOption(optionName: string, optionValue: string): Promise<WPOption> {
        const response = await fetch(`/wp-json/pallet-calculator/v1/options/${optionName}`, {
            method: 'PUT',
            headers: this.getWPHeaders(),
            body: JSON.stringify({ value: optionValue })
        });
        return response.json();
    }
    //#endregion

}
